import { useState, useEffect } from 'react';
import Modal from '../Modal';
import styled from 'styled-components';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  // tag is coming from MultiValueLabel props.data and has the following structure instead of Tag object:
  tag: any; // { value, label, color }
  onSave: (tagId: string, newName: string, color?: string) => void;
  onDelete: (tagId: string) => void;
}

const ColorPickerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const ColorPreview = styled.div<{ color: string }>`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: ${props => props.color};
  border: 2px solid #e5e7eb;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const ColorInput = styled.input`
  width: 70px;
  height: 40px;
  padding: 4px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

function TagsModal({ isOpen, onClose, tag, onSave, onDelete }: ModalProps) {
  const [name, setName] = useState(tag.label);
  const [color, setColor] = useState(tag.color);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ name?: string }>({});

  useEffect(() => {
    setName(tag.label);
    setColor(tag.color);
    setErrors({});
  }, [tag]);

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      onSave(tag.value, name.trim(), color);
      onClose();
    } catch (error) {
      console.error('Error saving tag:', error);
      setErrors({ name: 'Kunde inte spara taggen' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasChanges = name !== tag?.label || color !== tag?.color;

  return (
    <Modal isOpen={isOpen} closeModal={onClose} className="tag-modal">
      <h4>
        Redigera tagg
      </h4>

      <div>
        <Label htmlFor="tag-name">Taggens namn *</Label>
        <input
          id="tag-name"
          type="text"
          value={name}
          onKeyDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            setName(e.target.value);
            if (errors.name) setErrors({ ...errors, name: undefined });
          }}
          placeholder="Ange taggnamn"
          disabled={isSubmitting}
        />
        {errors.name && (
          <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
            {errors.name}
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="tag-color">Färg</Label>
        <ColorPickerContainer>
          <ColorPreview color={color} />
          <ColorInput
            id="tag-color"
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={isSubmitting}
          />
          <span style={{ fontSize: '14px', color: '#6b7280' }}>
            {color.toUpperCase()}
          </span>
        </ColorPickerContainer>
      </div>

      <div>
        <button
          className='sm cancel'
          onClick={onClose}
          disabled={isSubmitting}
        >
          Avbryt
        </button>

        <button
          className='sm remove'
          onClick={() => onDelete(tag.value)}
          disabled={isSubmitting}
        >
          Ta bort tagg
        </button>

        <button
          className='sm primary'
          onClick={handleSave}
          disabled={isSubmitting || !hasChanges}
        >
          {isSubmitting ? 'Sparar...' : 'Spara'}
        </button>
      </div>
    </Modal>
  );
}

export default TagsModal;
